// src/hr/portal/RemindersPage.js -- expiry reminders (mockup screen 4, spec §8.7 and §16.2 M7).
// Grouped Next 60 days / Later / Done. A reminder is General (label + due date) or Work authorization
// (document type, expires on, optional extension filed on): an extension filed on time adds 240 days,
// the same rule the server's reminderMath uses. Only labels and dates are stored -- never document
// numbers. HR gets the daily digest at 60 / 30 / 7 days (120 / 90 too for work authorization).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiBell, FiCheck, FiEdit2, FiInfo, FiPlus, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Card, Drawer, EmptyState, Field, Pill, Seg, Skeleton } from '../kit';
import { fullDate, fullName, isCurrent, workAuthEffectiveDate } from './portalUtils';
import { FormError } from './FormError';

const KINDS = [{ value: 'general', label: 'General' }, { value: 'workAuth', label: 'Work authorization' }];
const DOC_TYPES = [{ value: 'I-94', label: 'I-94' }, { value: 'EAD', label: 'EAD' }, { value: 'other', label: 'Other temporary' }];
const DOC_WORDS = { 'I-94': 'I-94', EAD: 'EAD', other: 'Other temporary' };
const WORK_AUTH_HINT = "Only for people whose work authorization expires — never for US citizens or green-card holders. Dates only; don't enter document numbers.";
const LABEL_HINT = "Keep it short. Don't enter document numbers.";
const EXTENSION_LATE = 'The extension must be filed by the expiry date';
const SOON_DAYS = 60;

function daysPill(r) {
  if (r.done) return { tone: 'ok', text: 'Done' };
  if (r.daysLeft === null || r.daysLeft === undefined) return { tone: 'mute', text: 'No date' };
  if (r.daysLeft < 0) {
    const n = Math.abs(r.daysLeft);
    return { tone: 'bad', text: `${n} ${n === 1 ? 'day' : 'days'} overdue` };
  }
  if (r.daysLeft === 0) return { tone: 'bad', text: 'Today' };
  return { tone: r.daysLeft <= SOON_DAYS ? 'warn' : 'mute', text: `${r.daysLeft} ${r.daysLeft === 1 ? 'day' : 'days'}` };
}

// The line under a reminder's title: its effective date, and for a work authorization what it rests on.
function whenLine(r, today) {
  if (r.kind !== 'workAuth') return r.dueDate ? fullDate(r.dueDate) : 'No date';
  const doc = DOC_WORDS[r.docType] || 'Work authorization';
  if (r.extensionFiledOn && r.expiresOn) {
    const verb = r.expiresOn < today ? 'expired' : 'expires';
    return `${doc} · ${verb} ${fullDate(r.expiresOn)} · extension filed · good until ${fullDate(r.dueDate)}`;
  }
  return `${doc} · ${r.dueDate && r.dueDate < today ? 'expired' : 'expires'} ${fullDate(r.dueDate)}`;
}

// ---------------------------------------------------------------------------------------------
// Add / edit drawer
// ---------------------------------------------------------------------------------------------

function initialForm(reminder) {
  if (!reminder) return { employeeId: '', kind: 'general', label: '', dueDate: '', docType: 'EAD', expiresOn: '', extensionFiledOn: '' };
  return {
    employeeId: reminder.employee ? reminder.employee.id : '',
    kind: reminder.kind || 'general',
    label: reminder.label || '',
    dueDate: reminder.kind === 'workAuth' ? '' : reminder.dueDate || '',
    docType: reminder.docType || 'EAD',
    expiresOn: reminder.expiresOn || '',
    extensionFiledOn: reminder.extensionFiledOn || ''
  };
}

function ReminderForm({ reminder, people, onClose, onDone }) {
  const editing = !!reminder;
  const start = useMemo(() => initialForm(reminder), [reminder]);
  const [form, setForm] = useState(start);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((x) => ({ ...x, [key]: undefined }));
    setError('');
  };

  const choices = useMemo(() => (people || []).filter(isCurrent)
    .map((p) => ({ value: p.id, label: fullName(p) }))
    .sort((a, b) => a.label.localeCompare(b.label)), [people]);
  const workAuth = form.kind === 'workAuth';
  const late = workAuth && form.extensionFiledOn && form.expiresOn && form.extensionFiledOn > form.expiresOn;
  const effective = workAuth && !late ? workAuthEffectiveDate(form.expiresOn, form.extensionFiledOn) : '';

  const submit = async () => {
    if (busy) return;
    const found = {};
    if (!editing && !form.employeeId) found.employeeId = 'Pick a person';
    if (!workAuth && !form.label.trim()) found.label = 'Enter a label';
    if (!workAuth && !form.dueDate) found.dueDate = 'Pick a valid due date';
    if (workAuth && !form.expiresOn) found.expiresOn = 'Pick a valid expiry date';
    if (late) found.extensionFiledOn = EXTENSION_LATE;
    setErrors(found);
    if (Object.keys(found).length) return;

    let body;
    if (!editing) {
      body = workAuth
        ? { employeeId: form.employeeId, kind: 'workAuth', docType: form.docType, expiresOn: form.expiresOn }
        : { employeeId: form.employeeId, kind: 'general', label: form.label.trim(), dueDate: form.dueDate };
      if (workAuth && form.extensionFiledOn) body.extensionFiledOn = form.extensionFiledOn;
      if (workAuth && form.label.trim()) body.label = form.label.trim();
    } else {
      body = {};
      if (form.label.trim() !== start.label) body.label = form.label.trim();
      const keys = workAuth ? ['docType', 'expiresOn', 'extensionFiledOn'] : ['dueDate'];
      keys.forEach((k) => { if (form[k] !== start[k]) body[k] = form[k]; });
      if (Object.keys(body).length === 0) { onClose(); return; }
    }

    setBusy(true);
    try {
      if (editing) await hr.admin.reminders.update(reminder.id, body);
      else await hr.admin.reminders.create(body);
      const who = choices.find((c) => c.value === form.employeeId);
      onDone(editing ? 'Reminder saved' : `Reminder added${who ? ` for ${who.label}` : ''}`);
    } catch (err) {
      setError(errorText(err, "Couldn't save the reminder. Try again."));
      setBusy(false);
    }
  };

  const who = editing && reminder.employee ? reminder.employee : null;

  return (
    <Drawer
      open
      onClose={onClose}
      title={editing ? 'Edit reminder' : 'Add reminder'}
      subtitle="HR gets an email before it's due. Labels and dates only."
      width={440}
      onSubmit={submit}
      className="hrp-rmd"
      footer={(
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" icon={editing ? FiCheck : FiPlus} loading={busy}>{editing ? 'Save reminder' : 'Add reminder'}</Btn>
        </>
      )}
    >
      {who ? (
        <div className="hrp-ltd__who">
          <Avatar id={who.id} name={who.name} />
          <div className="hrp-ltd__whotxt"><b>{who.name}</b><span>{workAuth ? 'Work authorization' : 'General reminder'}</span></div>
        </div>
      ) : (
        <>
          <Field label="Person" type="select" value={form.employeeId} onChange={(e) => set('employeeId', e.target.value)} error={errors.employeeId}
            options={[{ value: '', label: choices.length ? 'Choose someone' : 'Loading people…', disabled: true }, ...choices]} />
          <div className="hrp-field">
            <span className="hrp-field__label" id="hrp-rmd-kind">Type</span>
            <Seg labelledBy="hrp-rmd-kind" options={KINDS} value={form.kind} onChange={(v) => set('kind', v)} />
          </div>
        </>
      )}

      {workAuth ? (
        <>
          <p className="hrp-rmd__note" role="note"><FiInfo aria-hidden="true" /><span>{WORK_AUTH_HINT}</span></p>
          <div className="hrp-field">
            <span className="hrp-field__label" id="hrp-rmd-doc">Document</span>
            <Seg labelledBy="hrp-rmd-doc" options={DOC_TYPES} value={form.docType} onChange={(v) => set('docType', v)} />
          </div>
          <Field label="Label" labelNote="(optional)" value={form.label} onChange={(e) => set('label', e.target.value)} maxLength={80}
            placeholder={`Work authorization (${form.docType})`} hint={LABEL_HINT} />
          <div className="hrp-two">
            <Field label="Expires on" type="date" value={form.expiresOn} onChange={(e) => set('expiresOn', e.target.value)} error={errors.expiresOn} />
            <Field label="Extension filed on" labelNote="(optional)" type="date" value={form.extensionFiledOn}
              onChange={(e) => set('extensionFiledOn', e.target.value)} error={errors.extensionFiledOn || (late ? EXTENSION_LATE : undefined)} />
          </div>
          <div className="hrp-req__prev hrp-rmd__eff" data-testid="hrp-rm-effective" aria-live="polite">
            <div className="hrp-lab hrp-req__prevlab">Reminders count down to</div>
            {effective ? (
              <div className="hrp-rmd__effv">
                <b>
                  {'Effective: '}
                  <time dateTime={effective}>{fullDate(effective)}</time>
                  {form.extensionFiledOn ? ' (240-day extension)' : ''}
                </b>
                <span>{form.extensionFiledOn ? `Expires ${fullDate(form.expiresOn)}; an extension filed on time adds 240 days.` : 'The expiry date. Add the date an extension was filed to count 240 more days.'}</span>
              </div>
            ) : (
              <div className="hrp-rmd__effv"><span>{late ? 'An extension filed after the expiry date doesn\'t count.' : 'Pick the expiry date.'}</span></div>
            )}
            <p className="hrp-req__note">HR gets emails 120, 90, 60, 30 and 7 days before.</p>
          </div>
        </>
      ) : (
        <>
          <Field label="Label" value={form.label} onChange={(e) => set('label', e.target.value)} error={errors.label} maxLength={80}
            placeholder="e.g. Probation ends, First-aid certification" hint={LABEL_HINT} />
          <Field label="Due date" type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} error={errors.dueDate}
            hint="HR gets emails 60, 30 and 7 days before." />
        </>
      )}

      <FormError>{error}</FormError>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------------------------

function ReminderRow({ reminder, today, onEdit, onChanged }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const keepRef = useRef(null);
  const who = reminder.employee || { id: reminder.id, name: '' };
  const pill = daysPill(reminder);
  const title = `${reminder.label}${who.name ? ` · ${who.name}` : ''}`;

  useEffect(() => { if (confirming && keepRef.current) keepRef.current.focus(); }, [confirming]);

  const act = async (kind) => {
    setBusy(kind);
    setError('');
    try {
      if (kind === 'remove') await hr.admin.reminders.remove(reminder.id);
      else await hr.admin.reminders.update(reminder.id, { done: !reminder.done });
      await onChanged(kind === 'remove' ? 'Reminder removed' : reminder.done ? 'Reminder reopened' : 'Marked done');
    } catch (err) {
      setError(errorText(err, "Couldn't save that. Try again."));
      setBusy(null);
    }
  };

  return (
    <li className="hrp-rm__it" data-done={reminder.done ? 'true' : undefined}>
      <div className="hrp-rm__row">
        <Avatar id={who.id} name={who.name} />
        <div className="hrp-rm__txt">
          <b>{title}</b>
          <span>{whenLine(reminder, today)}</span>
        </div>
        <Pill tone={pill.tone} className="hrp-rm__days">{pill.text}</Pill>
        <span className="hrp-rm__acts">
          <button type="button" className="hrp-iconbtn hrp-rm__btn" disabled={!!busy}
            aria-label={reminder.done ? `Reopen ${title}` : `Mark ${title} done`} title={reminder.done ? 'Reopen' : 'Mark done'} onClick={() => act('done')}>
            {reminder.done ? <FiRotateCcw aria-hidden="true" /> : <FiCheck aria-hidden="true" />}
          </button>
          {!reminder.done && (
            <button type="button" className="hrp-iconbtn hrp-rm__btn" disabled={!!busy} aria-label={`Edit ${title}`} title="Edit" onClick={() => onEdit(reminder)}>
              <FiEdit2 aria-hidden="true" />
            </button>
          )}
          <button type="button" className="hrp-iconbtn hrp-rm__btn hrp-ent__btn--del" disabled={!!busy} aria-label={`Remove ${title}`} title="Remove" onClick={() => setConfirming(true)}>
            <FiTrash2 aria-hidden="true" />
          </button>
        </span>
      </div>
      {confirming && (
        <div className="hrp-tl__confirm" role="group" aria-label={`Remove ${title}?`}>
          <span>Remove this reminder? No more emails go out for it.</span>
          <span className="hrp-tl__btns">
            <Btn ref={keepRef} variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy === 'remove'}>Keep</Btn>
            <Btn variant="danger" size="sm" icon={FiTrash2} loading={busy === 'remove'} onClick={() => act('remove')}>Remove</Btn>
          </span>
        </div>
      )}
      {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </li>
  );
}

function Group({ title, rows, today, onEdit, onChanged }) {
  if (rows.length === 0) return null;
  return (
    <section className="hrp-rm__grp">
      <h2 className="hrp-group">{title}</h2>
      <ul className="hrp-rm__list" aria-label={title}>
        {rows.map((r) => <ReminderRow key={r.id} reminder={r} today={today} onEdit={onEdit} onChanged={onChanged} />)}
      </ul>
    </section>
  );
}

function HowCard() {
  return (
    <Card feat delay={1} as="aside" className="hrp-rm-how" aria-labelledby="hrp-rm-how-title">
      <h2 className="hrp-card__title" id="hrp-rm-how-title">How reminders work</h2>
      <ul className="hrp-rm-how__list">
        <li><b>General</b><span>Emails at 60, 30 and 7 days before the due date.</span></li>
        <li><b>Work authorization</b><span>Emails at 120, 90, 60, 30 and 7 days. An extension filed on time adds 240 days.</span></li>
        <li><b>One email a day</b><span>HR gets a single digest at 8:00 AM with everything due.</span></li>
        <li><b>Dates only</b><span>Never document numbers or scans.</span></li>
      </ul>
    </Card>
  );
}

export function RemindersPage({ today, people, onToast }) {
  const [state, setState] = useState({ status: 'loading', rows: null });
  const [drawer, setDrawer] = useState(null); // { reminder } -- null reminder = add
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const rows = await hr.admin.reminders.list();
      setState({ status: 'ready', rows: Array.isArray(rows) ? rows : [] });
    } catch (err) {
      setState((s) => ({ ...s, status: 'error' }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const all = (people && people.data && people.data.people) || [];
  const rows = state.rows || [];
  const open = rows.filter((r) => !r.done);
  const soon = open.filter((r) => r.daysLeft !== null && r.daysLeft !== undefined && r.daysLeft <= SOON_DAYS);
  const later = open.filter((r) => !(r.daysLeft !== null && r.daysLeft !== undefined && r.daysLeft <= SOON_DAYS));
  const done = rows.filter((r) => r.done);

  // Changes move the menu's "expiring soon" badge too, so the people list (its source) reloads.
  const changed = async (text) => {
    onToast(text);
    await load();
    if (people && people.reload) people.reload();
  };
  const saved = (text) => {
    setDrawer(null);
    changed(text);
  };

  let body;
  if (!state.rows && state.status === 'error') {
    body = (
      <EmptyState compact icon={FiAlertCircle} title="Couldn't load reminders" action={<Btn variant="ghost" size="sm" onClick={load}>Try again</Btn>}>
        Something went wrong on our side.
      </EmptyState>
    );
  } else if (!state.rows) {
    body = <Skeleton label="Loading reminders" lines={5} height={18} />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState compact icon={FiBell} title="No reminders yet" action={<Btn size="sm" icon={FiPlus} onClick={() => setDrawer({ reminder: null })}>Add the first one</Btn>}>
        Work visas, probation ends, certifications — HR gets emails before they're due.
      </EmptyState>
    );
  } else {
    body = (
      <>
        {state.status === 'error' && (
          <div className="hrp-form-err hrp-rm__stale" role="alert">
            <FiAlertCircle aria-hidden="true" /><span>This list didn't refresh.</span>
            <Btn variant="ghost" size="sm" onClick={load}>Try again</Btn>
          </div>
        )}
        {open.length === 0 && <p className="hrp-rm__none">Nothing open. Everything here is done.</p>}
        <Group title="Next 60 days" rows={soon} today={today} onEdit={(r) => setDrawer({ reminder: r })} onChanged={changed} />
        <Group title="Later" rows={later} today={today} onEdit={(r) => setDrawer({ reminder: r })} onChanged={changed} />
        {done.length > 0 && (
          <div className="hrp-rm__donebar">
            <button type="button" className="hrp-link" aria-expanded={showDone} onClick={() => setShowDone((v) => !v)}>
              {showDone ? 'Hide done' : `Show done (${done.length})`}
            </button>
          </div>
        )}
        {showDone && <Group title="Done" rows={done} today={today} onEdit={(r) => setDrawer({ reminder: r })} onChanged={changed} />}
      </>
    );
  }

  return (
    <div className="hrp-page hrp-rm">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Reminders</h1>
          <p className="hrp-sub">
            {state.rows ? `${soon.length} due in the next 60 days · ${open.length} open` : 'Expiry dates HR needs to act on, with emails before each one.'}
          </p>
        </div>
        <div className="hrp-page-head__acts">
          <Btn icon={FiPlus} onClick={() => setDrawer({ reminder: null })}>Add reminder</Btn>
        </div>
      </div>
      <div className="hrp-rm__cols">
        <Card delay={0} className="hrp-rm__card">{body}</Card>
        <HowCard />
      </div>
      {drawer && <ReminderForm reminder={drawer.reminder} people={all} onClose={() => setDrawer(null)} onDone={saved} />}
    </div>
  );
}

export default RemindersPage;
